/**
 * RecentActivityCard - Shows recent activity counts or neutral empty state
 * 
 * Purely presentational - displays activity data passed as props.
 * Only shows section if there's meaningful activity to display.
 * 
 * To revert: Remove this component usage from Dashboard.
 */

import { Card } from '@/components/ui/card';
import { Bell, Briefcase, MessageSquare, Clock } from 'lucide-react';

export function RecentActivityCard({ 
  newJobsCount = 0, 
  unreadMessagesCount = 0,
  navigate 
}) {
  const hasActivity = newJobsCount > 0 || unreadMessagesCount > 0;
  
  const activityItems = [];
  
  if (unreadMessagesCount > 0) {
    activityItems.push({
      id: 'messages',
      icon: MessageSquare,
      label: `${unreadMessagesCount} unread message${unreadMessagesCount > 1 ? 's' : ''}`,
      action: () => navigate('/messages'),
      color: 'text-brand bg-brand-light'
    });
  }
  
  if (newJobsCount > 0) {
    activityItems.push({
      id: 'jobs',
      icon: Briefcase,
      label: `${newJobsCount} new job${newJobsCount > 1 ? 's' : ''} posted`,
      action: () => navigate('/jobs'),
      color: 'text-green-600 bg-green-50'
    });
  }

  return (
    <Card className="p-6 border border-slate-200 shadow-sm rounded-2xl" data-testid="recent-activity-card">
      <div className="flex gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
          <Bell className="w-6 h-6 text-brand" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-slate-900 mb-3">Quiet Updates</h3>
          {!hasActivity ? (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              <Clock className="w-5 h-5 text-slate-400" />
              <p className="text-sm text-slate-500">No recent activity yet</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {activityItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={item.action}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                    data-testid={`activity-${item.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default RecentActivityCard;
